/* eslint-disable no-nested-ternary */
import { useMutation } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { DownloadIcon, DriveIcon } from '../../../assets';
import { PERMISSIONS } from '../../../config/constants';
import { replaceIds, ROUTES } from '../../../config/routes/config';
import { LINK_CONTRACT_DOCUMENT } from '../../../graphql/proposal/mutation';
import { useNotification } from '../../../hooks/useNotification';
import { usePermissions } from '../../../hooks/usePermissions';
import type {
  ProposalAction,
  ProposalInfluencer,
  ResSignedDocument,
} from '../../../types/proposal';
import { formatNumberMoney } from '../../../utils/numberFormatter';
import { downloadDocumentClick } from '../../../utils/utils';
import type { SingleProposalOutletContext } from '../../../views/SingleProposal';
import { CampaignInfluencers } from '../../campaigns/Sidebar/CampaignInfluencers';
import { CampaignPrice } from '../../campaigns/Sidebar/CampaignPrice';
import { HiredPlatforms } from '../../campaigns/Sidebar/HiredPlatforms';
import { Button } from '../../common/Button';
import { Warning } from '../../common/Warning';
import { BrandImage } from '../../images/BrandImage';
import { SocialNetworkImage } from '../../images/SocialNetworkImage';
import { UsersImages } from '../../images/UserImages';
import ProposalStatusDropdown from '../../inputs/ProposalStatusDropdown';

type TContractForm = {
  contract_url: string;
};

function packSubtotal(actions: ProposalAction[] | undefined): number {
  return (actions ?? []).reduce((sum, a) => sum + (a.total_amount ?? 0), 0);
}

function InfluencerActionsRow({ inf }: { inf: ProposalInfluencer }) {
  const { t } = useTranslation('common');
  const isRejected = inf.status === 'rejected';
  const packs = (inf.packs ?? [])
    .slice()
    .sort((a, b) => a.position - b.position);
  const legacyActions = inf.actions ?? [];
  const hasPacks = packs.length > 0;

  return (
    <div
      className={`flex flex-col gap-2 pb-3 border-b border-white/10 last:border-b-0 last:pb-0 ${isRejected ? 'opacity-50' : ''}`}
    >
      <div className="flex flex-row items-center gap-3">
        <UsersImages
          userCount={1}
          userImages={[inf.profile_picture || '']}
          size="size-[38px]"
        />
        <span
          className={`text-sm font-semibold ${isRejected ? 'text-gray-400 line-through' : 'text-white'}`}
        >
          {inf.name || inf.id}
        </span>
      </div>
      {isRejected ? (
        <span className="text-[11px] text-gray-500 italic pl-1">
          {t('proposals.influencerRejected')}
        </span>
      ) : hasPacks ? (
        <div className="flex flex-col gap-2 pl-1">
          {packs.map((pack) => {
            const isSelected = inf.selected_pack_id === pack.id;
            const actions = pack.actions ?? [];
            return (
              <div
                key={pack.id}
                className={`flex flex-col gap-1 rounded-[4px] px-2 py-1.5 ${
                  isSelected
                    ? 'bg-influentia-light-blue/10 border border-influentia-light-blue'
                    : ''
                }`}
              >
                <span
                  className={`text-[11px] font-semibold uppercase tracking-wide ${
                    isSelected ? 'text-influentia-light-blue' : 'text-gray-400'
                  }`}
                >
                  {pack.name || t('singleCampaign.packNamePlaceholder')}
                  {isSelected ? ' ✓' : ''}
                </span>
                {actions.length === 0 ? (
                  <span className="text-[11px] text-gray-400 italic">
                    {t('singleCampaign.noActions')}
                  </span>
                ) : (
                  actions.map((action) => (
                    <div
                      key={action.id}
                      className="flex flex-row gap-2 items-center text-[12px] text-white"
                    >
                      <SocialNetworkImage
                        socialNetwork={action.platform}
                        size="size-[16px]"
                      />
                      <span>
                        {t(`actionType.${action.type}`)} -{' '}
                        {action.number_of_action}
                      </span>
                    </div>
                  ))
                )}
              </div>
            );
          })}
        </div>
      ) : legacyActions.length === 0 ? (
        <span className="text-[11px] text-gray-400 italic pl-1">
          {t('singleCampaign.noActions')}
        </span>
      ) : (
        <div className="flex flex-col gap-1 pl-1">
          {legacyActions.map((action) => (
            <div
              key={action.id}
              className="flex flex-row gap-2 items-center text-[12px] text-white"
            >
              <SocialNetworkImage
                socialNetwork={action.platform}
                size="size-[16px]"
              />
              <span>
                {t(`actionType.${action.type}`)} - {action.number_of_action}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function SingleProposalGeneralTab() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { checkPermission } = usePermissions();
  const { notifyWarning, notifySaveSuccess } = useNotification();
  const { getAccessTokenSilently } = useAuth0();

  const {
    proposal,
    updateProposal,
    approveProposal,
    refetch,
    updating,
    approving,
    generateProposalPdf,
    generating,
    generateProposalDoc,
    generatingDoc,
  } = useOutletContext<SingleProposalOutletContext>();

  const contractMethods = useForm<TContractForm>({
    values: proposal
      ? { contract_url: proposal.contract_url || '' }
      : undefined,
  });
  const {
    register: regContract,
    handleSubmit: handleContractSubmit,
    formState: { isDirty: isContractDirty },
  } = contractMethods;

  const [linkContractDocument, { loading: linking }] =
    useMutation<ResSignedDocument>(LINK_CONTRACT_DOCUMENT);

  const handleDownloadPdf = async () => {
    if (!proposal.pdf_url) return;
    await downloadDocumentClick(
      proposal.pdf_url,
      await getAccessTokenSilently(),
      undefined,
      `${proposal.name}.pdf`,
    );
  };

  const onSaveContract = (formData: TContractForm) => {
    updateProposal({
      variables: { id: proposal.id, contract_url: formData.contract_url },
      onCompleted: () => {
        notifySaveSuccess();
        if (formData.contract_url) {
          linkContractDocument({
            variables: {
              proposalId: proposal.id,
              contract_url: formData.contract_url,
            },
            onCompleted: () => refetch(),
            onError: () => refetch(),
          });
        } else {
          refetch();
        }
      },
      onError: () => notifyWarning(t('proposals.approveError')),
    });
  };

  const handleApprove = () => {
    approveProposal({
      variables: { id: proposal.id },
      onCompleted: (res) => {
        if (res.approveProposal.code === 200) {
          notifySaveSuccess();
          refetch();
          if (res.approveProposal.data?.campaign_recurrent_id) {
            navigate(
              replaceIds({
                url: ROUTES.SINGLE_CAMPAIGN_RECURRENT_SETTINGS,
                params: [
                  {
                    campaignId: res.approveProposal.data.campaign_recurrent_id,
                  },
                ],
              }),
            );
          } else if (res.approveProposal.data?.campaign_id) {
            navigate(
              replaceIds({
                url: ROUTES.SINGLE_CAMPAIGN_GENERAL,
                params: [
                  { campaignId: res.approveProposal.data.campaign_id },
                  { id: 'campaign' },
                ],
              }),
            );
          }
        } else {
          notifyWarning(
            res.approveProposal.error || t('proposals.approveError'),
          );
        }
      },
      onError: () => notifyWarning(t('proposals.approveError')),
    });
  };

  const handleReject = () => {
    updateProposal({
      variables: { id: proposal.id, status: 'rejected' },
      onCompleted: () => {
        notifySaveSuccess();
        refetch();
      },
      onError: () => notifyWarning(t('proposals.rejectError')),
    });
  };

  const isApproved = proposal.status === 'approved';
  const isRejected = proposal.status === 'rejected';
  const hasPdf = Boolean(proposal.pdf_url);
  const hasDoc = Boolean(proposal.drive_document_url);

  const activeInfluencers = proposal.ProposalUsers.filter(
    (inf) => inf.status !== 'rejected',
  );
  // Every active influencer that has pricing packs must have one selected
  // before approving — otherwise it's ambiguous which pack becomes the
  // real campaign. Influencers with no packs at all (legacy, packless
  // actions only) don't block approval.
  const missingPackSelection = activeInfluencers.filter(
    (inf) => (inf.packs?.length ?? 0) > 0 && !inf.selected_pack_id,
  );
  const canApprove = missingPackSelection.length === 0;

  const signedDoc = proposal.signedDocument;
  const contractStatus = signedDoc?.signed_at
    ? 'contract_signed'
    : 'contract_pending';

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      {/* LEFT column */}
      <div className="flex flex-col gap-4">
        {/* Caja 1 — brand info + sidebar data */}
        <div className="bg-dark-blue rounded-10 p-5 flex flex-col gap-4">
          <div className="bg-influentia-black rounded-10 p-4 flex flex-row gap-4 items-center">
            <BrandImage
              image={proposal.brand_profile_picture || undefined}
              size="size-[76px]"
            />
            <div className="flex flex-col gap-1 w-full">
              <span className="text-white font-bold">
                {proposal.brand_name || '-'}
              </span>
              <span className="text-gray-400 text-sm">
                {proposal.month || '-'}
              </span>
              <ProposalStatusDropdown
                defaultStatus={proposal.status}
                proposalId={proposal.id}
                refetch={refetch}
              />
            </div>
          </div>
          <CampaignPrice
            price={proposal.money ?? 0}
            currency={proposal.currency ?? undefined}
          />
          <HiredPlatforms
            platforms={Array.from(
              new Set(
                proposal.ProposalUsers.flatMap((u) =>
                  (u.actions ?? []).map((a) => a.platform),
                ).filter(Boolean),
              ),
            )}
          />
          <CampaignInfluencers
            influencers={proposal.ProposalUsers.map((u) => ({
              id: u.id,
              name: u.name || u.id,
            }))}
          />
        </div>

        {/* Caja 2 — PROPUESTA + Generar */}
        <div className="bg-dark-blue rounded-10 p-5 flex flex-col gap-4">
          <p className="text-white font-semibold text-xs uppercase tracking-widest">
            {t('proposals.title')}
          </p>
          {hasPdf && (
            <div className="flex flex-col gap-1.5">
              <span className="text-gray-400 text-[10px] uppercase tracking-wide">
                {t('proposals.completeProposalGroup')}
              </span>
              <div className="flex flex-row gap-2">
                <Button
                  id="downloadProposalPdf"
                  style="light-blue-2"
                  text={t('proposals.downloadPdf')}
                  customClassnames="flex-1 justify-center"
                  iconLeft={<DownloadIcon className="size-4" />}
                  onClick={handleDownloadPdf}
                />
                <a
                  href={proposal.drive_url ?? '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex flex-row items-center justify-center gap-1.5 px-3 py-1.5 rounded-[4px] text-sm font-normal bg-influentia-yellow text-dark-blue hover:opacity-80"
                >
                  <DriveIcon className="size-4" />
                  {t('proposals.openInDrive')}
                </a>
              </div>
            </div>
          )}
          {hasDoc && proposal.drive_document_url && (
            <div className="flex flex-col gap-1.5">
              <span className="text-gray-400 text-[10px] uppercase tracking-wide">
                {t('proposals.verticalProposalGroup')}
              </span>
              <div className="flex flex-row gap-2">
                <a
                  href={proposal.drive_document_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex flex-row items-center justify-center gap-1.5 px-3 py-1.5 rounded-[4px] text-sm font-normal bg-influentia-yellow text-dark-blue hover:opacity-80"
                >
                  <DriveIcon className="size-4" />
                  {t('proposals.openVerticalDoc')}
                </a>
              </div>
            </div>
          )}
          {proposal.missing_from_ppt &&
            proposal.missing_from_ppt.length > 0 && (
              <Warning
                warning={t('proposals.missingFromPpt', {
                  names: proposal.missing_from_ppt.join(', '),
                })}
              />
            )}
          <Button
            id="generateProposal"
            style="light-blue-2"
            text={
              hasPdf
                ? t('proposals.regenerateProposal')
                : t('proposals.generateProposal')
            }
            customClassnames="w-full justify-center"
            loading={generating}
            disabled={generating || generatingDoc}
            onClick={() =>
              generateProposalPdf({ variables: { proposalId: proposal.id } })
            }
          />
          <Button
            id="generateProposalDoc"
            style="light-blue-2"
            text={
              hasDoc
                ? t('proposals.regenerateVerticalDoc')
                : t('proposals.generateVerticalDoc')
            }
            customClassnames="w-full justify-center"
            loading={generatingDoc}
            disabled={generatingDoc || generating}
            onClick={() =>
              generateProposalDoc({ variables: { proposalId: proposal.id } })
            }
          />
        </div>

        {/* Approve / Reject — fuera de las cajas */}
        {checkPermission(PERMISSIONS.UPDATE_PROPOSAL) &&
          !isApproved &&
          !isRejected && (
            <div className="flex flex-col gap-y-2">
              <div className="flex flex-row gap-x-2">
                <Button
                  id="approveProposal"
                  style="light-blue"
                  text={t('proposals.approveProposal')}
                  customClassnames="flex-1 justify-center !bg-green-status !text-dark-blue"
                  loading={approving}
                  disabled={!canApprove}
                  onClick={handleApprove}
                />
                <Button
                  id="rejectProposal"
                  style="black"
                  text={t('proposals.rejectProposal')}
                  customClassnames="flex-1 justify-center !bg-red-status !text-white"
                  onClick={handleReject}
                />
              </div>
              {!canApprove && (
                <Warning warning={t('proposals.approveMissingPackSelection')} />
              )}
            </div>
          )}

        {/* Go to campaign if approved */}
        {isApproved && proposal.campaign_id && (
          <Button
            id="goToCampaign"
            style="light-blue"
            text={t('proposals.goToCampaign')}
            customClassnames="w-full justify-center"
            onClick={() =>
              navigate(
                replaceIds({
                  url: ROUTES.SINGLE_CAMPAIGN_GENERAL,
                  params: [
                    { campaignId: proposal.campaign_id! },
                    { id: 'campaign' },
                  ],
                }),
              )
            }
          />
        )}
      </div>

      {/* MIDDLE — influencer list with their actions */}
      <div className="bg-dark-blue rounded-10 p-5 flex flex-col gap-4">
        <p className="text-white font-semibold text-xs uppercase tracking-widest">
          {t('global.influencers')}
        </p>
        {proposal.ProposalUsers.length === 0 ? (
          <p className="text-gray-400 text-sm italic">{t('global.no-data')}</p>
        ) : (
          <div className="flex flex-col gap-3">
            {(() => {
              const active = proposal.ProposalUsers.filter(
                (inf) => inf.status !== 'rejected',
              );
              const rejected = proposal.ProposalUsers.filter(
                (inf) => inf.status === 'rejected',
              );

              const sortedActive = active.sort((a, b) =>
                (a.name ?? '').localeCompare(b.name ?? ''),
              );
              const sortedRejected = rejected.sort((a, b) =>
                (a.name ?? '').localeCompare(b.name ?? ''),
              );

              return [...sortedActive, ...sortedRejected].map((inf) => (
                <InfluencerActionsRow key={inf.id} inf={inf} />
              ));
            })()}
          </div>
        )}
      </div>

      {/* RIGHT: prices + contract — 2 cajas separadas */}
      <div className="flex flex-col gap-4">
        {/* Caja PRECIOS */}
        <div className="rounded-10 p-5 flex flex-col gap-3 text-white bg-dark-blue">
          <p className="font-semibold text-sm uppercase tracking-widest text-white">
            {t('proposals.fields.prices')}
          </p>
          {proposal.ProposalUsers.length === 0 ? (
            <p className="text-gray-400 text-sm italic">
              {t('global.no-data')}
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {(() => {
                const active = proposal.ProposalUsers.filter(
                  (inf) => inf.status !== 'rejected',
                );
                const sortedActive = active.sort((a, b) =>
                  (a.name ?? '').localeCompare(b.name ?? ''),
                );

                return sortedActive.map((inf) => {
                  const packs = (inf.packs ?? [])
                    .slice()
                    .sort((a, b) => a.position - b.position);
                  return (
                    <div
                      key={inf.id}
                      className="flex flex-col gap-1.5 py-2 border-b border-gray-100 last:border-b-0"
                    >
                      <div className="flex flex-row justify-between items-center">
                        <div className="flex flex-row items-center gap-2">
                          <UsersImages
                            userCount={1}
                            userImages={[inf.profile_picture || '']}
                            size="size-[38px]"
                          />
                          <span className="text-sm font-medium">
                            {inf.name || inf.id}
                          </span>
                        </div>
                        <span className="text-sm">
                          {inf.total_amount != null
                            ? formatNumberMoney(
                                inf.total_amount,
                                undefined,
                                proposal.currency ?? undefined,
                              )
                            : '-'}
                          {inf.influencer_variable_type &&
                            inf.influencer_variable_type !== 'No' && (
                              <span className="">
                                {' '}
                                + {inf.influencer_variable_type}
                                {inf.total_variable_amount != null
                                  ? ` - ${formatNumberMoney(
                                      inf.total_variable_amount,
                                      undefined,
                                      proposal.currency ?? undefined,
                                    )}`
                                  : ''}
                              </span>
                            )}
                        </span>
                      </div>
                      {packs.length > 0 && (
                        <div className="flex flex-col gap-0.5 pl-[46px]">
                          {packs.map((pack) => {
                            const isSelected = inf.selected_pack_id === pack.id;
                            return (
                              <div
                                key={pack.id}
                                className={`flex flex-row justify-between text-[11px] ${
                                  isSelected
                                    ? 'font-semibold text-influentia-light-blue'
                                    : 'text-gray-400'
                                }`}
                              >
                                <span>
                                  {pack.name ||
                                    t('singleCampaign.packNamePlaceholder')}
                                  {isSelected ? ' ✓' : ''}
                                </span>
                                <span>
                                  {formatNumberMoney(
                                    pack.total_amount ??
                                      packSubtotal(pack.actions),
                                    undefined,
                                    proposal.currency ?? undefined,
                                  )}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </div>

        {/* Caja CONTRATO */}
        {/* <div className="rounded-10 p-5 flex flex-col gap-3 text-white bg-dark-blue">
          <FormProvider {...contractMethods}>
            <form
              onSubmit={handleContractSubmit(onSaveContract)}
              className="flex flex-col gap-3"
            >
              <p className="font-semibold text-sm uppercase tracking-widest text-white">
                {t('proposals.fields.contract')}
              </p>
              <div className="flex flex-row items-end gap-2">
                <div className="flex-1">
                  <TextInput
                    register={regContract}
                    registerName="contract_url"
                    type="text"
                    label={t('proposals.fields.contractUrl')}
                    placeholder={t('proposals.fields.contractUrlPlaceholder')}
                  />
                </div>
                {isContractDirty && (
                  <div className="mb-1">
                    <Button
                      id="saveContractUrl"
                      style="light-blue"
                      text={t('global.save')}
                      customClassnames="flex-none h-[41px]"
                      loading={updating || linking}
                      disabled={updating || linking}
                      type="submit"
                    />
                  </div>
                )}
              </div>
              <div className="flex flex-row items-center justify-between gap-4">
                <p className="font-semibold text-xs uppercase tracking-widest text-white flex-none">
                  {t('proposals.fields.contractStatus')}
                </p>
                <StatusText status={contractStatus} type="contract" />
              </div>
            </form>
          </FormProvider>
        </div> */}
      </div>
    </div>
  );
}
